import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface UserOrganization {
  id: string;
  name: string;
  description?: string;
  user_role: string;
}

// Query key factory
export const userOrganizationKeys = {
  all: ['user-organizations'] as const,
  lists: () => [...userOrganizationKeys.all, 'list'] as const,
  list: (userId: string) => [...userOrganizationKeys.lists(), userId] as const,
};

/**
 * Fetch user's organizations with their role in each organization
 * This is different from OrganizationContext which doesn't include user roles
 */
export function useUserOrganizations() {
  const { user } = useAuth();

  return useQuery({
    queryKey: userOrganizationKeys.list(user?.id || ''),
    queryFn: async () => {
      if (!user) {
        return [];
      }

      const { data: orgMembers, error } = await supabase
        .from('organization_members')
        .select(`
          organization_id,
          role,
          organizations (
            id,
            name,
            description
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      if (!orgMembers) return [];

      // Map to include user_role
      return orgMembers.map(member => ({
        ...(Array.isArray(member.organizations) ? member.organizations[0] : member.organizations),
        user_role: member.role
      })) as UserOrganization[];
    },
    enabled: !!user,
  });
}
